import { Badge, DocumentWidget, MonetaryAmount, Tab, Tabs } from '@components'
import { ACCOUNT_TYPES } from '@constants'
import {
  hasHistoryBeforeNoCFO,
  isVATObliged,
  PermissionSet,
  useBusinessContext,
  usePermission
} from '@context'
import { IAccount } from '@query'
import { useVATCode } from '@root/query/hooks/useVATCodes'
import { findOptionByValue, formatCurrency } from '@utils'
import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaInfoCircle, FaStream } from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'

interface Props {
  account: IAccount
}

export const AccountOverview: React.FC<Props> = ({ account }) => {
  const [t] = useTranslation()
  const { data: business } = useBusinessContext()
  const hasBookkeepingViewPermission = usePermission(PermissionSet.BOOKKEEPING_VIEW)
  const vat_obliged = isVATObliged(business)
  const has_history = hasHistoryBeforeNoCFO(business)

  const accountType = findOptionByValue(ACCOUNT_TYPES, account.type)
  const vatOption = useVATCode(account.default_vat_code)

  return (
    <div key={'account-' + account.id}>
      <Tabs>
        <Tab key="info" icon={<FaInfoCircle />} name="Tiedot">
          <div>
            <DetailRow>
              <StyledHeader>{t('accounts.overview.name')}</StyledHeader>
              <StyledValue>{account.name || '-'}</StyledValue>
            </DetailRow>

            {account.description && (
              <DetailRow>
                <StyledHeader>{t('accounts.overview.description')}</StyledHeader>
                <AccountDecription>
                  <ReactMarkdown linkTarget="_blank">{account.description}</ReactMarkdown>
                </AccountDecription>
              </DetailRow>
            )}
          </div>

          <DetailRow>
            <StyledHeader>{t('accounts.overview.type')}</StyledHeader>
            <StyledValue>{t(accountType.labelKey)}</StyledValue>
          </DetailRow>

          {account.default_vat_code !== 3 && vat_obliged && (
            <>
              <DetailRow>
                <StyledHeader>{t('accounts.overview.vatCode')}</StyledHeader>
                <StyledValue>
                  {vatOption && <Badge type="success">{vatOption.label}</Badge>}
                </StyledValue>
              </DetailRow>

              <DetailRow>
                <StyledHeader>{t('accounts.overview.vatRate')}</StyledHeader>
                <StyledValue>
                  <Badge type="info">
                    {t('accounts.overview.vatValue', { vatRate: account.default_vat_rate })}
                  </Badge>
                </StyledValue>
              </DetailRow>
            </>
          )}

          <DetailRow>
            <StyledHeader>{t('accounts.overview.balance')}</StyledHeader>
            <StyledValue>
              <MonetaryAmount value={account.balance} showColor={true} />
            </StyledValue>
          </DetailRow>

          {has_history && (
            <DetailRow>
              <StyledHeader>{t('accounts.overview.openingBalance')}</StyledHeader>
              <StyledValue>{formatCurrency(account.opening_balance)}</StyledValue>
            </DetailRow>
          )}
        </Tab>

        {hasBookkeepingViewPermission && (
          <Tab key="docs" icon={<FaStream />} name="Tapahtumat">
            <DocumentWidget
              header={t('accounts.documentsTitle')}
              noData={t('accounts.documentsNoData')}
              accountId={account.id}
              height={400}
            />
          </Tab>
        )}
      </Tabs>
    </div>
  )
}

const DetailRow = styled(motion.div).attrs({ layout: 'position' })`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledHeader = styled.p`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
  align-self: flex-start;
`

const StyledValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  margin: 0;
`

const AccountDecription = styled.div`
  color: ${({ theme }) => theme.colors.neutralBlack};
  width: 100%;
`
